import React from 'react';
import PropTypes from 'prop-types';

import { PerformanceChartBase, Header } from './PerformanceChart.styles';
import { PerformanceChartHeader } from './PerformanceChartHeader';
import { TimelineFilter } from '../Timeline/TimelineFilter';
import { TimelineChart } from '../Timeline/TimelineChart';
import { TimelineLabel } from '../Timeline/TimelineLabel';
import { PerformanceChartRow } from './PerformanceChartRow';
import { WithScroller } from '../Scroller/WithScroller';
import { useNavigate, useLocation } from 'react-router-dom';
import { Logger } from 'common/logger';
import { useRematchDispatch, useRematchSelector } from 'models/rematchStore';

const headerWidth = 500;
const chartMinWidth = 1000;
const chartMaxWidth = 2000;

export const PerformanceChart = ({ chartWidth, theme }) => {
    const navigate = useNavigate();
    const dispatch = useRematchDispatch();
    const {
        performance: { filter, sort, turbineStatuses: timelineData, turbineSummaries: turbineData },
        menuBar: { selectedDate },
    } = useRematchSelector((state) => state);

    const location = useLocation();
    const useScroller = chartWidth <= chartMinWidth;
    const childChartWidth = useScroller ? chartMaxWidth : chartWidth;

    const onClickTurbine = (turbineId) => {
        Logger.info(`Showing turbine: ${turbineId}`);
        dispatch.turbineSummary.selectTurbine({ payload: { selectedTurbine: turbineId } });
        navigate({
            pathname: `${location.pathname}/${turbineId}`,
            search: location.search,
        });
    };

    const tickLabelComponent = (
        <TimelineLabel
            data={turbineData}
            width={headerWidth}
            rowComponent={<PerformanceChartRow onClick={onClickTurbine} />}
        />
    );
    const handleSort = (sortKey) => {
        dispatch.performance.sortStatuses({ sortKey });
    };

    const ChartHeaderContent = () => (
        <PerformanceChartHeader
            width={headerWidth}
            sort={sort}
            handleSort={handleSort}
            hideDivider={useScroller}
            chartWidth={useScroller ? chartMaxWidth : ''}
        />
    );

    const handleFilter = (filterKey) => () => {
        dispatch.performance.filterStatuses({ filterKey });
    };

    return (
        <PerformanceChartBase data-testid="performance-chart">
            <Header>
                {!useScroller && <ChartHeaderContent />}
                <TimelineFilter filter={filter} handleFilter={handleFilter} />
            </Header>

            <WithScroller useScroller={useScroller}>
                {() => (
                    <>
                        {useScroller && <ChartHeaderContent />}
                        <TimelineChart
                            chartDay={selectedDate}
                            chartWidth={childChartWidth}
                            timelineData={timelineData}
                            turbineData={turbineData}
                            tickLabelComponent={tickLabelComponent}
                            itemLabelWidth={headerWidth}
                            theme={theme}
                        />
                    </>
                )}
            </WithScroller>
        </PerformanceChartBase>
    );
};

PerformanceChart.displayName = 'PerformanceChart';

PerformanceChart.propTypes = {
    chartWidth: PropTypes.number,
};

PerformanceChart.defaultProps = {};
